import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Plus, ArrowDown, ArrowUp, ArrowsLeftRight } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

// Each payment kind and the entity fields it requires.
const KINDS = [
  { value: "customer_paid_us", label: "Customer paid us", dir: "in", needs: ["customer"] },
  { value: "customer_paid_broker", label: "Customer paid broker (on our behalf)", dir: "in", needs: ["customer", "broker"] },
  { value: "cash_to_broker", label: "Cash advance to broker", dir: "out", needs: ["broker"] },
  { value: "broker_pays_supplier", label: "Broker paid supplier (on our behalf)", dir: "transfer", needs: ["broker", "supplier"] },
  { value: "pay_supplier", label: "Direct payment to supplier", dir: "out", needs: ["supplier"] },
  { value: "broker_returned_cash", label: "Broker returned cash to us", dir: "in", needs: ["broker"] },
];
const KIND_MAP = Object.fromEntries(KINDS.map((k) => [k.value, k]));

const EMPTY = {
  kind: "customer_paid_us", customer_id: "", supplier_id: "", broker_id: "", order_id: "",
  amount: 0, currency: "PKR", method: "bank_transfer", reference: "", notes: "",
};

const DIR_META = {
  in: { text: "IN", bg: "#D1FAE5", fg: "#065F46", bd: "#10B981", Icon: ArrowDown },
  out: { text: "OUT", bg: "#FEE2E2", fg: "#991B1B", bd: "#EF4444", Icon: ArrowUp },
  transfer: { text: "TRANSFER", bg: "#DBEAFE", fg: "#1E40AF", bd: "#3B82F6", Icon: ArrowsLeftRight },
};

export default function Payments() {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/payments").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/brokers").then((r) => setBrokers(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data));
  }, []);

  const canManage = hasRole("admin", "accounts");
  const kindDef = KIND_MAP[form.kind];

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        kind: form.kind,
        amount: Number(form.amount),
        currency: form.currency,
        method: form.method,
        reference: form.reference,
        notes: form.notes,
        customer_id: kindDef.needs.includes("customer") ? form.customer_id : null,
        supplier_id: kindDef.needs.includes("supplier") ? form.supplier_id : null,
        broker_id: kindDef.needs.includes("broker") ? form.broker_id : null,
        order_id: form.order_id || null,
      };
      await api.post("/payments", payload);
      toast.success("Payment recorded");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const custMap = Object.fromEntries(customers.map((c) => [c.id, c]));
  const supMap = Object.fromEntries(suppliers.map((s) => [s.id, s]));
  const brkMap = Object.fromEntries(brokers.map((b) => [b.id, b]));

  const partyLabel = (p) => {
    const parts = [];
    if (p.customer_id && custMap[p.customer_id]) parts.push(`Cust: ${custMap[p.customer_id].name}`);
    if (p.broker_id && brkMap[p.broker_id]) parts.push(`Brk: ${brkMap[p.broker_id].name}`);
    if (p.supplier_id && supMap[p.supplier_id]) parts.push(`Sup: ${supMap[p.supplier_id].name}`);
    return parts.join(" → ") || "—";
  };

  const setNeeded = (need) => kindDef.needs.includes(need);

  const canSave = () => {
    if (!form.amount || Number(form.amount) <= 0) return false;
    if (setNeeded("customer") && !form.customer_id) return false;
    if (setNeeded("supplier") && !form.supplier_id) return false;
    if (setNeeded("broker") && !form.broker_id) return false;
    return true;
  };

  return (
    <div className="space-y-5" data-testid="payments-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mini">Money In · Money Out · Broker Flows</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Payments</h1>
        </div>
        {canManage && (
          <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="record-payment-btn" className="h-9 rounded-sm bg-black hover:bg-zinc-800 text-white">
            <Plus size={14} className="mr-1" /> Record Payment
          </Button>
        )}
      </div>

      <div className="card-sharp overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="label-mini px-4 py-3">Date</th>
              <th className="label-mini px-4 py-3">Flow</th>
              <th className="label-mini px-4 py-3">Parties</th>
              <th className="label-mini px-4 py-3">Method</th>
              <th className="label-mini px-4 py-3">Reference</th>
              <th className="label-mini px-4 py-3 text-right">Amount</th>
              <th className="label-mini px-4 py-3 text-right">PKR Equiv.</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const def = KIND_MAP[p.kind];
              const dir = DIR_META[def?.dir || "in"];
              const DirIcon = dir.Icon;
              return (
                <tr key={p.id} className="border-t border-zinc-100" data-testid={`payment-row-${p.id}`}>
                  <td className="px-4 py-3 text-xs font-mono">{fmtDate(p.date)}</td>
                  <td className="px-4 py-3">
                    <span className="status-badge inline-flex items-center gap-1" style={{ background: dir.bg, color: dir.fg, borderColor: dir.bd }}>
                      <DirIcon size={11} weight="bold" /> {dir.text}
                    </span>
                    <div className="text-[11px] text-zinc-500 mt-1">{p.label || def?.label || p.kind}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{partyLabel(p)}</td>
                  <td className="px-4 py-3 text-xs capitalize">{(p.method || "").replace("_", " ")}</td>
                  <td className="px-4 py-3 text-xs font-mono">{p.reference || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono tnum">{fmtMoney(p.amount, p.currency)}</td>
                  <td className="px-4 py-3 text-right font-mono tnum font-semibold">{fmtMoney(p.amount_pkr || 0, "")}</td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-zinc-500 text-sm">No payments yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="payment-form-dialog">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Payment Flow">
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v, customer_id: "", supplier_id: "", broker_id: "" })}>
                <SelectTrigger className="rounded-sm h-9" data-testid="pay-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (<SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>

            {setNeeded("customer") && (
              <Field label="Customer" required>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="pay-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} · {c.code}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            {setNeeded("broker") && (
              <Field label="Broker" required>
                <Select value={form.broker_id} onValueChange={(v) => setForm({ ...form, broker_id: v })}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="pay-broker"><SelectValue placeholder="Select broker" /></SelectTrigger>
                  <SelectContent>
                    {brokers.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name} · {b.code}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            {setNeeded("supplier") && (
              <Field label="Supplier" required>
                <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="pay-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name} · {s.code}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <Field label="Order (optional)">
              <Select value={form.order_id || "none"} onValueChange={(v) => setForm({ ...form, order_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm h-9" data-testid="pay-order"><SelectValue placeholder="No order" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No order</SelectItem>
                  {orders
                    .filter((o) => !form.customer_id || o.customer_id === form.customer_id)
                    .map((o) => (<SelectItem key={o.id} value={o.id}>{o.code}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount" required>
                <Input data-testid="pay-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm h-9 font-mono" />
              </Field>
              <Field label="Currency">
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PKR">PKR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="CNY">CNY</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Method">
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Reference / Txn ID">
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="rounded-sm h-9 font-mono" />
              </Field>
            </div>
            <Field label="Notes">
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm h-9" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-payment-btn" onClick={save} disabled={saving || !canSave()} className="rounded-sm bg-black text-white hover:bg-zinc-800">
              {saving ? "Saving…" : "Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="label-mini">{label}{required && <span className="text-red-600">*</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);
